import * as React from 'react'

import { cn } from '@/lib/utils'

// Recuo horizontal do conteúdo: 8px no mobile e 40px a partir do tablet — os
// mesmos do UIKIT (px-[8px] no grid 4col, px-[40px] nos grids 8col/12col).
const sectionPadding = 'px-[var(--spacing-scale-base)] min-[576px]:px-[var(--spacing-scale-5x)]'

type HeaderProps = React.ComponentProps<'header'> & {
  // Densidade do cabeçalho (mapeia .small/.medium/.large → --header-padding).
  size?: 'small' | 'medium' | 'large'
  // Cabeçalho reduzido (mapeia .br-header.compact): padding small, logo de
  // 16px, sem assinatura e com título um degrau menor.
  compact?: boolean
  // Fixa no topo ao rolar (mapeia [data-sticky]).
  sticky?: boolean
}

const headerPadding = {
  // --header-padding-*: base / 2x / 3x (tokens de espaçamento do DS).
  small: 'py-[var(--spacing-scale-base)]',
  medium: 'py-[var(--spacing-scale-2x)]',
  large: 'py-[var(--spacing-scale-3x)]',
} as const

function Header({
  className,
  size = 'medium',
  compact = false,
  sticky = false,
  ...props
}: HeaderProps) {
  return (
    <header
      data-slot="header"
      data-compact={compact ? 'true' : undefined}
      data-sticky={sticky ? 'true' : undefined}
      className={cn(
        'relative flex flex-col',
        'bg-[var(--background)] [box-shadow:var(--surface-shadow-sm)]',
        sectionPadding,
        // .compact força a densidade small (--header-padding-small).
        headerPadding[compact ? 'small' : size],
        // As três variáveis que o DS resolve por breakpoint e que os slots
        // abaixo consomem — é assim que o próprio core.css escala o cabeçalho.
        // --header-logo-size: medium (24px) → large (40px) a partir de 576px;
        // no compact é sempre small (16px).
        compact
          ? '[--header-logo-size:16px]'
          : '[--header-logo-size:24px] min-[576px]:[--header-logo-size:40px]',
        // --header-title-size: base → up-02 (≥576px) → up-03 (≥1280px).
        // No compact o DS desce um degrau: up-01 e up-02.
        '[--header-title-size:var(--font-size-scale-base)]',
        compact
          ? 'min-[576px]:[--header-title-size:var(--font-size-scale-up-01)] min-[1280px]:[--header-title-size:var(--font-size-scale-up-02)]'
          : 'min-[576px]:[--header-title-size:var(--font-size-scale-up-02)] min-[1280px]:[--header-title-size:var(--font-size-scale-up-03)]',
        // --header-subtitle-size: down-01 → base (≥1280px).
        '[--header-subtitle-size:var(--font-size-scale-down-01)] min-[1280px]:[--header-subtitle-size:var(--font-size-scale-base)]',
        // No compact o UIKIT não mostra assinatura (nem o divisor que a
        // acompanha), só a marca reduzida.
        compact && '[&_[data-slot=header-sign]]:hidden',
        sticky && 'sticky top-0 z-[var(--z-index-layer-2)] [transition:all_150ms_ease]',
        className
      )}
      {...props}
    />
  )
}

// .header-top: linha superior — logo/assinatura à esquerda e ações à direita.
function HeaderTop({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="header-top"
      className={cn('flex items-center justify-end', className)}
      {...props}
    />
  )
}

// .header-bottom: linha inferior, menu/título à esquerda e busca à direita.
function HeaderBottom({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="header-bottom"
      className={cn(
        'flex items-center justify-between',
        'mt-[var(--spacing-scale-base)]',
        className
      )}
      {...props}
    />
  )
}

// .header-logo: marca (img/svg) + assinatura, alinhadas com gap.
function HeaderLogo({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="header-logo"
      className={cn(
        'flex flex-1 items-center gap-[var(--spacing-scale-2x)]',
        // .header-logo img/svg { max-height: var(--header-logo-size) } — 24px
        // no mobile, 40px a partir de 576px, 16px no compact.
        '[&_img]:max-h-[var(--header-logo-size)] [&_svg]:max-h-[var(--header-logo-size)]',
        className
      )}
      {...props}
    />
  )
}

// .header-sign: assinatura textual. Só aparece a partir de 576px e vem
// precedida do divisor vertical que o UIKIT desenha entre marca e assinatura
// (.header-logo .br-divider, oculto abaixo desse breakpoint).
function HeaderSign({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="header-sign"
      className={cn(
        'hidden overflow-hidden text-[color:var(--color)]',
        '[font-size:var(--font-size-scale-base)] [font-weight:var(--font-weight-medium)]',
        'min-[576px]:flex min-[576px]:items-center min-[576px]:gap-[var(--spacing-scale-2x)]',
        // Divisor de 1px por 40px, na cor de borda do DS (gray-20).
        "before:content-[''] min-[576px]:before:block min-[576px]:before:h-[var(--spacing-scale-5x)] min-[576px]:before:w-px min-[576px]:before:shrink-0 min-[576px]:before:bg-[var(--border-color)]",
        className
      )}
      {...props}
    />
  )
}

// .header-actions: grupo de ações (links, funções, login) à direita.
function HeaderActions({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="header-actions"
      className={cn('flex items-center justify-end gap-[var(--spacing-scale-base)]', className)}
      {...props}
    />
  )
}

// .header-links: hiperlinks de acesso rápido. No DS eles ficam num dropdown
// abaixo de 1280px e viram uma linha de links no desktop — sem JS próprio,
// aqui só a linha do desktop é exibida.
function HeaderLinks({ className, ...props }: React.ComponentProps<'nav'>) {
  return (
    <nav
      data-slot="header-links"
      className={cn(
        'hidden min-[1280px]:flex min-[1280px]:items-center',
        // .br-item:not(:last-child) { margin-right: var(--spacing-scale-3x) }
        'gap-[var(--spacing-scale-3x)]',
        // Links em down-01/semibold e no azul interativo sobre fundo claro.
        '[&_a]:text-[color:var(--interactive-light)] [&_a]:no-underline',
        '[&_a]:[font-size:var(--font-size-scale-down-01)]',
        '[&_a]:[font-weight:var(--font-weight-semi-bold)]',
        '[&_a:hover]:underline',
        '[&_a]:outline-none [&_a:focus-visible]:[outline:var(--focus-width)_dashed_var(--focus-color)] [&_a:focus-visible]:[outline-offset:var(--focus-offset)]',
        className
      )}
      {...props}
    />
  )
}

// .header-functions: atalhos de funcionalidade em botão circular de 32px.
// Como os links, só aparecem a partir de 1280px; o UIKIT separa o grupo dos
// hiperlinks por um divisor vertical.
function HeaderFunctions({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="header-functions"
      className={cn(
        'hidden min-[1280px]:flex min-[1280px]:items-center',
        'gap-[var(--spacing-scale-base)] pl-[var(--spacing-scale-base)]',
        // Divisor entre hiperlinks e funcionalidades (40px, gray-20).
        "before:block before:h-[var(--spacing-scale-5x)] before:w-px before:shrink-0 before:bg-[var(--border-color)] before:content-['']",
        // .br-button.circle no tamanho small: alvo de 32px, ícone no azul.
        '[&_button]:inline-flex [&_button]:size-[var(--spacing-scale-4x)] [&_button]:items-center [&_button]:justify-center',
        '[&_button]:rounded-full [&_button]:text-[color:var(--interactive)]',
        '[&_button:hover]:[background-image:var(--hover-effect)]',
        className
      )}
      {...props}
    />
  )
}

// .header-menu: gatilho do menu (drawer) + info (título/subtítulo). A partir
// de 576px o DS alinha o grupo pelo topo e desloca o gatilho em 3px para
// acompanhar a linha do título, que cresceu.
function HeaderMenu({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="header-menu"
      className={cn(
        'flex flex-1 items-center gap-[var(--spacing-scale-2x)]',
        'min-[576px]:items-start min-[576px]:[&>[data-slot=menu-trigger]]:mt-[3px]',
        className
      )}
      {...props}
    />
  )
}

// .header-info: agrupa título e subtítulo.
function HeaderInfo({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="header-info"
      className={cn('min-[576px]:pt-[var(--spacing-scale-half)] min-[1280px]:pt-0', className)}
      {...props}
    />
  )
}

// .header-title: título do site/órgão. Renderiza um <p> por padrão para não
// competir com o <h1> de conteúdo; passe asChild via className se precisar.
function HeaderTitle({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="header-title"
      className={cn(
        'm-0 text-[color:var(--color)]',
        '[font-size:var(--header-title-size)]',
        className
      )}
      {...props}
    />
  )
}

// .header-subtitle: subtítulo. Aparece a partir de 576px; no UIKIT vem em
// gray-70, um degrau abaixo do título.
function HeaderSubtitle({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="header-subtitle"
      className={cn(
        'm-0 hidden min-[576px]:block',
        '[font-weight:var(--font-weight-medium)] text-[color:var(--gray-70)]',
        'mt-[var(--spacing-scale-half)] [font-size:var(--header-subtitle-size)]',
        className
      )}
      {...props}
    />
  )
}

type HeaderSearchProps = React.ComponentProps<'div'> & {
  // Abre a busca sobre o cabeçalho abaixo de 1280px (mapeia .header-search
  // .active, acionado pelo header-search-trigger do DS). No desktop a busca é
  // sempre visível e esta prop não tem efeito.
  active?: boolean
}

// .header-search: área de busca. Encaixe o Input aqui; rotule por
// `aria-label`/`role="search"`. Abaixo de 1280px o DS a trata como uma camada
// que cobre o cabeçalho inteiro e só aparece quando ativada; a partir daí ela
// volta ao fluxo, com o campo em 385px de largura mínima. Sem foco, o fundo do
// campo é --gray-2 e a borda some, como no UIKIT.
function HeaderSearch({ className, role = 'search', active = false, ...props }: HeaderSearchProps) {
  return (
    <div
      data-slot="header-search"
      role={role}
      data-active={active ? 'true' : undefined}
      className={cn(
        'absolute inset-0 z-[var(--z-index-layer-1)] items-center',
        'bg-[var(--background)] px-[var(--spacing-scale-2x)]',
        active ? 'flex' : 'hidden',
        'min-[1280px]:static min-[1280px]:z-auto min-[1280px]:flex min-[1280px]:p-0',
        'gap-[var(--spacing-scale-base)]',
        '[&_input]:border-transparent [&_input:not(:focus)]:bg-[var(--gray-2)]',
        'min-[1280px]:[&_input]:min-w-[385px]',
        className
      )}
      {...props}
    />
  )
}

export {
  Header,
  HeaderActions,
  HeaderBottom,
  HeaderFunctions,
  HeaderInfo,
  HeaderLinks,
  HeaderLogo,
  HeaderMenu,
  HeaderSearch,
  HeaderSign,
  HeaderSubtitle,
  HeaderTitle,
  HeaderTop,
}
